import PublicLayout from "@/components/layout/PublicLayout";
import PricingSection from "@/components/sections/PricingSection";
import FaqPreview from "@/components/sections/FaqPreview";
import { Check, Coins, Mail, TrendingUp, RefreshCcw } from "lucide-react";
import Seo, { serviceSchema } from "@/components/Seo";

export default function Prijzen() {
  return (
    <PublicLayout>
      <Seo
        title="Prijzen — OfferteExpert"
        description="€4,99 per analyse of €9,99 voor een 3-pack. Geen abonnement. Eerlijke prijs voor een eerlijke offertecheck."
        jsonLd={serviceSchema}
      />
      <section className="oc-section bg-oc-bg pb-0">
        <div className="oc-container max-w-3xl">
          <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">Prijzen · particulieren</span>
          <h1 className="font-display font-bold text-[40px] sm:text-[52px] leading-tight tracking-tight text-oc-primary mt-3">
            Eén heldere prijs.<br className="hidden sm:block" /> Geen verrassingen.
          </h1>
          <p className="mt-5 text-oc-textSecondary text-[18px] leading-relaxed">
            Wij geloven in volledige transparantie. Je betaalt €4,99 per analyse en krijgt een volwaardig rapport. Geen abonnement, geen extra kosten, geen kleine lettertjes.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-oc-subtle border border-oc-border text-[13px] font-semibold text-oc-primary">
            <span className="w-2 h-2 rounded-full bg-oc-high" /> Enkel voor particulieren (B2C)
          </p>
        </div>
      </section>

      <PricingSection />

      <section className="oc-section bg-white border-t border-oc-border">
        <div className="oc-container max-w-4xl">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <span className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
                <Coins className="w-4 h-4" /> Bespaar tot 33% met het 3-pack
              </span>
              <h2 className="font-display font-bold text-oc-primary text-[28px] sm:text-[36px] mt-3 leading-tight">
                Waarom kiezen mensen voor de bundle?
              </h2>
              <p className="mt-4 text-oc-textSecondary text-[16px] leading-relaxed">
                Wie verbouwt of een grote investering doet, vraagt zelden één offerte. Met het 3-pack vergelijk je <strong className="text-oc-primary">drie offertes objectief tegenover elkaar</strong> — exact waarvoor OfferteExpert bedoeld is.
              </p>
              <ul className="mt-6 space-y-3 text-[15px] text-oc-textPrimary">
                <li className="flex items-start gap-3">
                  <TrendingUp className="w-4 h-4 text-oc-fair mt-1 shrink-0" />
                  <span><strong>Bespaar gemiddeld 33%</strong> ten opzichte van drie losse analyses (€9,99 vs €14,97).</span>
                </li>
                <li className="flex items-start gap-3">
                  <Coins className="w-4 h-4 text-oc-fair mt-1 shrink-0" />
                  <span><strong>12 maanden geldig</strong> — gebruik je credits wanneer een nieuwe offerte binnenkomt.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-oc-fair mt-1 shrink-0" />
                  <span>Na aankoop ontvang je een mail met een persoonlijke link naar <em className="font-mono">/credits</em>: één klik om je volgende offerte te uploaden zonder opnieuw te betalen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <RefreshCcw className="w-4 h-4 text-oc-fair mt-1 shrink-0" />
                  <span>Identieke kwaliteit als de losse analyse — volledige heatmap, PDF-rapport en onderhandelingstips per credit.</span>
                </li>
              </ul>
              <p className="mt-6 text-oc-textSecondary text-[13px] leading-relaxed italic">
                De prijspagina toont de 3-pack als <strong>"POPULAIR · BESPAAR 33%"</strong> met een aparte CTA naar de wizard met <code className="font-mono text-[12px] px-1 py-0.5 bg-oc-subtle rounded">?bundle=1</code> voorgeselecteerd. Dat verhoogt de gemiddelde transactiewaarde gemiddeld <strong>2 à 3 keer</strong>, en de mail-flow bij bundle-aankoop stuurt klanten meteen naar <em className="font-mono">/credits</em> om hun volgende offerte te uploaden zonder opnieuw te betalen.
              </p>
            </div>
            <div className="lg:col-span-2 bg-oc-subtle border border-oc-border rounded-oc p-7">
              <p className="text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">Reken-voorbeeld</p>
              <div className="mt-3 space-y-2 text-[14px]">
                <div className="flex justify-between"><span>3 losse analyses</span><span className="font-mono">€ 14,97</span></div>
                <div className="flex justify-between"><span>3-pack bundle</span><span className="font-mono text-oc-fair font-semibold">€ 9,99</span></div>
                <div className="border-t border-oc-border pt-2 mt-2 flex justify-between font-semibold text-oc-primary">
                  <span>Bespaard</span>
                  <span className="font-mono text-oc-fair">€ 2,98</span>
                </div>
              </div>
              <p className="mt-4 text-[12px] text-oc-textSecondary leading-relaxed">
                Vraag drie offertes en check ze alle drie — vergelijken zonder afpraten geeft je de beste onderhandelingspositie aan tafel.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="oc-section bg-white">
        <div className="oc-container max-w-3xl">
          <h2 className="font-display font-bold text-oc-primary text-[28px]">OfferteExpert vs. een adviesbureau</h2>
          <div className="mt-7 grid sm:grid-cols-2 gap-5">
            <div className="bg-oc-subtle border border-oc-border rounded-oc p-7">
              <p className="text-[13px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary">Adviesbureau</p>
              <p className="font-mono text-oc-primary font-semibold text-[36px] mt-3 leading-none">€ 250 — 600</p>
              <ul className="mt-5 space-y-2 text-oc-textPrimary text-[14px]">
                <Item>Persoonlijk consult</Item>
                <Item>Doorlooptijd 1-2 weken</Item>
                <Item>Hoge minimumkost</Item>
              </ul>
            </div>
            <div className="bg-oc-primary text-white rounded-oc p-7 relative overflow-hidden">
              <p className="text-[13px] uppercase tracking-[0.05em] font-semibold text-white/65">OfferteExpert</p>
              <p className="font-mono font-semibold text-[36px] mt-3 leading-none">€ 4,99</p>
              <ul className="mt-5 space-y-2 text-white text-[14px]">
                <Item dark>Volledig digitale flow</Item>
                <Item dark>Doorlooptijd: 60 sec</Item>
                <Item dark>Heatmap + onderhandelingstips</Item>
                <Item dark>PDF-rapport</Item>
              </ul>
            </div>
          </div>

          <div className="mt-10 rounded-oc border border-oc-border bg-oc-subtle p-7">
            <h3 className="font-display font-semibold text-oc-primary text-[18px]">Geld terug bij technische storing</h3>
            <p className="mt-2 text-oc-textSecondary text-[15px] leading-relaxed">
              Lukt de analyse niet door een technisch probleem? Dan krijg je je betaling integraal terug zonder vragen. Stuur een mail naar <a href="mailto:info@offerteexpert.be" className="text-oc-secondary underline">info@offerteexpert.be</a>.
            </p>
          </div>
        </div>
      </section>
      <FaqPreview />
    </PublicLayout>
  );
}

function Item({ children, dark }) {
  return (
    <li className="flex items-start gap-2">
      <Check className={`w-4 h-4 ${dark ? "text-white" : "text-oc-fair"} mt-0.5 shrink-0`} strokeWidth={3} />
      <span>{children}</span>
    </li>
  );
}
